import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';

import { BayclubsAlertService } from '@bayclubs/alert';
import { ErrorTranslationService, FormService } from '@bayclubs/utils';

import { campsenum } from '../../../../../models/enums.generated';
import { camps } from '../../../../../models/models.generated';
import { CampManagementApiService } from '../../../../../services/api/camp-management-api.service';
import { CampsEnumTranslationService } from '../../../../../services/camps-enum-translation.service';
import { CampsDetailsFormStateService } from '../../camp-details-form-state-service';

@Component({
    selector: 'app-camp-basic-info-edit',
    templateUrl: './camp-basic-info-edit.component.html',
    styleUrls: ['./camp-basic-info-edit.component.scss'],
    standalone: false,
})
export class CampBasicInfoEditComponent implements OnInit, OnDestroy {
    @Input() public form: UntypedFormGroup;

    @Input() context: camps.CampDefinitionCreationContext = <camps.CampDefinitionCreationContext>{
        categories: [],
        campStatuses: [],
        reservationDaysAheadLimitVariants: [],
        timeVariants: [],
        holdingAccounts: [],
    };

    parentCategories = signal<camps.CategoryModel[]>([]);
    subcategories = signal<camps.CategoryModel[]>([]);
    holdingAccounts = signal<camps.HoldingAccountModel[]>([]);
    revenueDepartmentCodes = signal<string[]>([]);

    private destroySubj: Subject<void> = new Subject<void>();

    constructor(
        private api: CampManagementApiService,
        private state: CampsDetailsFormStateService,
        private alert: BayclubsAlertService,
        private enumTranslation: CampsEnumTranslationService,
        private errorTranslate: ErrorTranslationService
    ) {}

    ngOnInit(): void {
        const campType = this.state.campType;

        const assignedParentId = this.categoryForm.get('parentId')?.value;
        const assignedSubcategoryId = this.categoryForm.get('id')?.value;

        const parentCategories = this.context.categories.filter((x) => !x.parentId && x.campType === campType);
        this.includeAssignedCategory(parentCategories, assignedParentId);
        this.parentCategories.set(parentCategories);

        const subcategories = this.context.categories.filter((x) => x.parentId);
        this.includeAssignedCategory(subcategories, assignedSubcategoryId);
        this.subcategories.set(subcategories);

        this.holdingAccounts.set(this.context.holdingAccounts ?? []);
        this.updateRevenueDepartmentCodes(this.form.get('holdingAccount').value);

        this.form
            .get('holdingAccount')
            .valueChanges.pipe(
                takeUntil(this.destroySubj),
                tap((holdingAccount: string) => this.updateRevenueDepartmentCodes(holdingAccount))
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroySubj.next();
        this.destroySubj.complete();
    }

    // Ensures the camp's currently-assigned category/subcategory is always present in
    // the option list, even when it would be excluded by the CampType or obsolete
    // filters, so the existing selection renders in edit mode (ticket 17332).
    private includeAssignedCategory(categories: camps.CategoryModel[], assignedId: string): void {
        if (!assignedId || categories.some((x) => x.id === assignedId)) {
            return;
        }

        const assigned = this.context.categories.find((x) => x.id === assignedId);
        if (assigned) {
            categories.push(assigned);
        }
    }

    private updateRevenueDepartmentCodes(holdingAccount: string): void {
        const codes = this.holdingAccounts().find((x) => x.code === holdingAccount)?.revenueDepartmentCodes ?? [];
        this.revenueDepartmentCodes.set(codes);

        const revenueDepartment = this.form.get('revenueDepartment');
        if (revenueDepartment.value && !codes.includes(revenueDepartment.value)) {
            revenueDepartment.setValue(null);
        }

        // Revenue departments are derived from the holding account, so there is
        // nothing to pick until one is selected.
        if (holdingAccount) {
            revenueDepartment.enable({ emitEvent: false });
        } else {
            revenueDepartment.disable({ emitEvent: false });
        }
    }

    public get hasHoldingAccount(): boolean {
        return !!this.form.get('holdingAccount').value;
    }

    public get categoryForm(): UntypedFormGroup {
        return this.form.controls['category'] as UntypedFormGroup;
    }

    public save() {
        const request = <camps.CampBasicInfoSaveRequest>{
            campId: this.form.get('id').value,
            name: this.form.get('name').value,
            status: this.form.get('status').value,
            categoryId: this.form.get('category.parentId').value,
            revenueDepartment: this.form.get('revenueDepartment').value,
            holdingAccount: this.form.get('holdingAccount').value,
        };

        const categoryId = this.form.get('category.id').value;

        if (categoryId) {
            request.categoryId = categoryId;
        }

        if (this.isChildcareCamp) {
            request.reservationDaysAheadLimit = this.form.get('reservationDaysAheadLimit').value;
            request.reservationDaysAheadLimitVariant = this.form.get('reservationDaysAheadLimitVariant').value;
        }

        this.api.saveInfo(request).subscribe((x) => {
            if (x.isSuccess) {
                this.alert.showOperationSuccess();
                this.state.setStatus(request.status);
                this.state.finishEdit(this.form);
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    public cancel() {
        this.state.finishEdit(this.form, false);
        FormService.markFormAsTouched(this.form);
    }

    public changeStatus(value: campsenum.CampStatus) {
        this.form.controls['status'].setValue(value);
    }

    public changeVariant(value: campsenum.ReservationDaysAheadLimitVariant) {
        this.form.controls['reservationDaysAheadLimitVariant'].setValue(value);
    }

    public translateStatus(status: campsenum.CampStatus) {
        return this.enumTranslation.campStatus(status);
    }

    public translateReservationDaysAheadLimitVariant(variant: campsenum.ReservationDaysAheadLimitVariant) {
        return this.enumTranslation.reservationDaysAheadLimitVariant(variant);
    }

    public isFormValid() {
        return FormService.isFormValid(this.form);
    }

    public get isChildcareCamp() {
        return this.state.isChildcareCamp();
    }
}
